import { useState, useEffect } from "react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const revenueData = [
  { month: "Jan", revenue: 42000, prev: 30000 }, { month: "Feb", revenue: 58000, prev: 38000 },
  { month: "Mar", revenue: 51000, prev: 45000 }, { month: "Apr", revenue: 73000, prev: 52000 },
  { month: "May", revenue: 68000, prev: 60000 }, { month: "Jun", revenue: 91000, prev: 70000 },
  { month: "Jul", revenue: 87000, prev: 75000 }, { month: "Aug", revenue: 105000, prev: 82000 },
  { month: "Sep", revenue: 98000, prev: 88000 }, { month: "Oct", revenue: 120000, prev: 95000 },
  { month: "Nov", revenue: 115000, prev: 102000 }, { month: "Dec", revenue: 138000, prev: 110000 },
];
const barData = [
  { name: "Q1", sales: 182, target: 200 }, { name: "Q2", sales: 245, target: 220 },
  { name: "Q3", sales: 212, target: 230 }, { name: "Q4", sales: 289, target: 260 },
];
const pieData = [
  { name: "Enterprise", value: 38, color: "#6EE7B7" }, { name: "Pro", value: 29, color: "#60A5FA" },
  { name: "Starter", value: 21, color: "#A78BFA" }, { name: "Free", value: 12, color: "#F472B6" },
];
const transactions = [
  { id: "TXN-8821", name: "Acme Corp", avatar: "AC", status: "Completed", date: "Dec 12, 2024", amount: "$12,400", type: "credit" },
  { id: "TXN-8820", name: "Nova Systems", avatar: "NS", status: "Pending", date: "Dec 11, 2024", amount: "$8,750", type: "credit" },
  { id: "TXN-8819", name: "Orbit Labs", avatar: "OL", status: "Completed", date: "Dec 10, 2024", amount: "$3,200", type: "debit" },
  { id: "TXN-8818", name: "Stellar Inc", avatar: "SI", status: "Failed", date: "Dec 09, 2024", amount: "$15,600", type: "credit" },
  { id: "TXN-8817", name: "Flux Tech", avatar: "FT", status: "Completed", date: "Dec 08, 2024", amount: "$6,890", type: "credit" },
];
const STAT_CARDS = [
  { label: "Total Revenue", value: "$1.38M", change: "+18.2%", up: true, accent: "#6EE7B7", icon: "💰" },
  { label: "Active Users", value: "94,210", change: "+12.5%", up: true, accent: "#60A5FA", icon: "👥" },
  { label: "Growth Rate", value: "23.8%", change: "+4.1%", up: true, accent: "#A78BFA", icon: "📈" },
  { label: "Total Orders", value: "18,472", change: "-2.3%", up: false, accent: "#F472B6", icon: "📦" },
];
const STATUS_COLOR = { Completed: "#6EE7B7", Pending: "#FBBF24", Failed: "#F87171" };
const STATUS_BG = { Completed: "rgba(110,231,183,0.1)", Pending: "rgba(251,191,36,0.1)", Failed: "rgba(248,113,113,0.1)" };
const AVATAR_COLORS = ["#6EE7B7","#60A5FA","#A78BFA","#F472B6","#FBBF24","#34D399"];

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(13,17,28,0.97)", border: "1px solid rgba(110,231,183,0.15)", borderRadius: 10, padding: "10px 16px", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
      <p style={{ color: "#8892a4", fontSize: 12, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontWeight: 700, fontSize: 13, margin: "2px 0" }}>
          {p.dataKey === "revenue" || p.dataKey === "prev" ? `$${(p.value/1000).toFixed(0)}k` : p.value} <span style={{ color: "#8892a4", fontWeight: 400, fontSize: 11 }}>{p.dataKey}</span>
        </p>
      ))}
    </div>
  );
}

function Card({ card, i }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), i * 100); }, []);
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "22px", position: "relative", overflow: "hidden", cursor: "pointer", transform: vis ? "none" : "translateY(16px)", opacity: vis ? 1 : 0, transition: "transform 0.5s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease, box-shadow 0.2s, border-color 0.2s" }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 0 1px ${card.accent}25, 0 16px 40px rgba(0,0,0,0.3)`; e.currentTarget.style.borderColor = `${card.accent}25`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}>
      <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: `radial-gradient(circle, ${card.accent}18 0%, transparent 70%)` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <span style={{ fontSize: 24 }}>{card.icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: card.up ? "#6EE7B7" : "#F87171", background: card.up ? "rgba(110,231,183,0.1)" : "rgba(248,113,113,0.1)", border: `1px solid ${card.up ? "rgba(110,231,183,0.2)" : "rgba(248,113,113,0.2)"}`, padding: "3px 9px", borderRadius: 20 }}>
          {card.up ? "▲" : "▼"} {card.change}
        </span>
      </div>
      <p style={{ color: "#8892a4", fontSize: 12, fontWeight: 600, marginBottom: 6, letterSpacing: "0.04em" }}>{card.label.toUpperCase()}</p>
      <p style={{ color: "#E8EDF5", fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>{card.value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState("30D");
  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#E8EDF5", letterSpacing: "-0.02em", marginBottom: 4 }}>Overview</h1>
          <p style={{ fontSize: 13, color: "#8892a4" }}>Track your key metrics and performance</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["7D","30D","90D","1Y"].map(r => (
            <button key={r} onClick={() => setRange(r)} style={{ fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 8, cursor: "pointer", background: range === r ? "rgba(110,231,183,0.12)" : "rgba(255,255,255,0.04)", border: range === r ? "1px solid rgba(110,231,183,0.25)" : "1px solid rgba(255,255,255,0.08)", color: range === r ? "#6EE7B7" : "#8892a4", transition: "all 0.2s", fontFamily: "inherit" }}
              onMouseEnter={e => { if (range !== r) e.currentTarget.style.background = "rgba(255,255,255,0.07)"; }}
              onMouseLeave={e => { if (range !== r) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>{r}</button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 20 }}>
        {STAT_CARDS.map((c, i) => <Card key={c.label} card={c} i={i} />)}
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 14 }}>
        <ChartBox title="Revenue Overview" sub="Monthly revenue vs previous year">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revenueData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6EE7B7" stopOpacity={0.25} /><stop offset="95%" stopColor="#6EE7B7" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.15} /><stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#8892a4" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#8892a4" }} axisLine={false} tickLine={false} tickFormatter={v => `$${v/1000}k`} />
              <Tooltip content={<Tip />} />
              <Area type="monotone" dataKey="prev" stroke="#60A5FA" strokeWidth={1.5} fill="url(#pg)" dot={false} strokeDasharray="4 4" />
              <Area type="monotone" dataKey="revenue" stroke="#6EE7B7" strokeWidth={2.5} fill="url(#rg)" dot={false} activeDot={{ r: 4, fill: "#6EE7B7", stroke: "#0A0E17", strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartBox>

        <ChartBox title="User Distribution" sub="By subscription tier">
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} dataKey="value" labelLine={false} stroke="none">
                {pieData.map((e, i) => <Cell key={i} fill={e.color} opacity={0.88} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v}%`, n]} contentStyle={{ background: "#0D1117", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} labelStyle={{ color: "#8892a4" }} itemStyle={{ color: "#E8EDF5" }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 4 }}>
            {pieData.map(p => (
              <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: "#8892a4" }}>{p.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#E8EDF5", marginLeft: "auto" }}>{p.value}%</span>
              </div>
            ))}
          </div>
        </ChartBox>
      </div>

      {/* Bar chart */}
      <div style={{ marginBottom: 20 }}>
        <ChartBox title="Quarterly Performance" sub="Sales vs targets">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={barData} barGap={4} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="bs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A78BFA" /><stop offset="100%" stopColor="#7C3AED" stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="bt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.6} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8892a4" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#8892a4" }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#8892a4", paddingTop: 8 }} />
              <Bar dataKey="sales" fill="url(#bs)" radius={[5,5,0,0]} maxBarSize={36} />
              <Bar dataKey="target" fill="url(#bt)" radius={[5,5,0,0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
      </div>

      {/* Transactions */}
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "20px 22px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: "#E8EDF5", marginBottom: 2 }}>Recent Transactions</h3>
            <p style={{ fontSize: 12, color: "#8892a4" }}>Latest activity across all accounts</p>
          </div>
          <button style={{ fontSize: 13, fontWeight: 600, color: "#6EE7B7", background: "rgba(110,231,183,0.08)", border: "1px solid rgba(110,231,183,0.2)", borderRadius: 8, padding: "6px 14px", cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(110,231,183,0.15)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(110,231,183,0.08)"}>View All</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                {["Client","Status","Date","Amount"].map(h => (
                  <th key={h} style={{ padding: "11px 22px", textAlign: h === "Amount" ? "right" : "left", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#4B5563" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, i) => (
                <tr key={tx.id} style={{ borderTop: "1px solid rgba(255,255,255,0.04)", transition: "background 0.15s", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.025)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "13px 22px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 9, background: `${AVATAR_COLORS[i%6]}15`, border: `1px solid ${AVATAR_COLORS[i%6]}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: AVATAR_COLORS[i%6], flexShrink: 0 }}>{tx.avatar}</div>
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: "#E8EDF5", margin: 0 }}>{tx.name}</p>
                        <p style={{ fontSize: 11, color: "#8892a4", margin: 0 }}>{tx.id}</p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "13px 22px" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: STATUS_COLOR[tx.status], background: STATUS_BG[tx.status], border: `1px solid ${STATUS_COLOR[tx.status]}25`, padding: "3px 10px", borderRadius: 20 }}>{tx.status}</span>
                  </td>
                  <td style={{ padding: "13px 22px", fontSize: 13, color: "#8892a4" }}>{tx.date}</td>
                  <td style={{ padding: "13px 22px", textAlign: "right", fontSize: 14, fontWeight: 700, color: tx.type === "credit" ? "#6EE7B7" : "#F87171" }}>{tx.type === "credit" ? "+" : "-"}{tx.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartBox({ title, sub, children }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "20px", transition: "border-color 0.2s" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, color: "#E8EDF5", margin: 0 }}>{title}</h3>
        {sub && <p style={{ fontSize: 12, color: "#8892a4", margin: "3px 0 0" }}>{sub}</p>}
      </div>
      {children}
    </div>
  );
}